import { Children, PropsWithChildren, ReactElement, ReactNode, isValidElement } from "react";

import { EmptyArray } from "../../../utilities/EmptyArray";
import { DataRow } from "../../DataRow";
import { BaseColumnProps } from "../BaseColumnProps";
import { SelectionColumn } from "../SelectionColumn";
import * as ColumnTypes from "../columnTypes";
import { getActions } from "../columnTypes/ActionsInternal/helpers";

/**
 * Gets the columns from the specified children.
 * @param children The children to get the columns from.
 * @param rows The table rows.
 * @returns The array of columns.
 */
export function getColumns<TRow extends object>(
  children: ReactNode | undefined,
  rows: ReadonlyArray<DataRow<TRow>>,
): ReadonlyArray<ReactElement<BaseColumnProps>> {
  const columns: Array<ReactElement<BaseColumnProps>> = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;

    const columnTypeNames = Object.keys(ColumnTypes);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!columnTypeNames.some((name) => child.type === (ColumnTypes as any)[name] || child.type === SelectionColumn)) {
      console.log("Table ignoring unsupported child component.");
      return;
    }
    columns.push(child as ReactElement<BaseColumnProps>);
  });

  if (columns.length === 0) return EmptyArray;

  return columns
    .sort((columnA, columnB) => {
      const { lock: lockA } = columnA.props;
      const { lock: lockB } = columnB.props;

      if (lockA === lockB) return 0;

      if (lockA === "left") return -1;
      if (lockA === undefined && lockB === "right") return -1;
      if (lockA === "right") return 1;

      return 0;
    })
    .filter((column) => {
      const { children: columnChildren } = column.props as PropsWithChildren<BaseColumnProps>;

      if (column.type !== ColumnTypes.Actions) return true;

      const actions = getActions(columnChildren);

      if (!rows.length) return true;

      const isVisibleInAnyRow = rows.some((row) => actions.some((action) => !action.visible || action.visible(row.id)));

      return isVisibleInAnyRow;
    });
}
